/**
 * Creates a field update function for managing deeply nested state in a React component.
 * This function dynamically updates a specific property inside a nested object stored in `useState`.
 *
 * @param {Function} setData - The React state setter function (`useState`).
 * @returns {Function} - A function that takes (section, keyPath, value) and updates the state.
 *
 * @example
 * const updateField = handleFieldChange(setData);
 * updateField("PageHeader", "header", "New Header"); // Updates data.Sec4.header
 * updateField("PageHeader", "item1.title", "New Title"); // Updates data.Sec4.item1.title
 */
export const handleFieldChange = (setData) => (section, keyPath, value) => {
  setData((prevData) => {
    const sectionData = prevData[section] || {};

    // Helper function to update deeply nested keys
    const updateNestedData = (obj, pathArray, newValue) => {
      if (pathArray.length === 1) {
        return { ...obj, [pathArray[0]]: newValue }; // Direct update
      }

      const key = pathArray[0];
      return {
        ...obj,
        [key]: updateNestedData(obj[key] || {}, pathArray.slice(1), newValue),
      };
    };

    return {
      ...prevData,
      [section]: updateNestedData(sectionData, keyPath.split("."), value),
    };
  });
};
